import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { DocChip } from "../shipment/DocChip";
import { FilePreviewModal } from "../shipment/FilePreviewModal";
import { TesisPickerField, useTesisPicker, resolveTesisId } from "../ui/TesisPickerField";
import { trpc } from "../../lib/trpc";

interface CompanyFormModalProps {
  open: boolean;
  /** Düzenlenecek cari id'si; null/undefined ise yeni kayıt. */
  companyId?: string | null;
  /** Yeni kayıtta önceden işaretli rol (ör. "+ Yeni Tedarikçi" akışı supplier). */
  defaultRole?: "supplier" | "customer" | "carrier" | "driver";
  onClose: () => void;
  /** Oluşturma sonrası (düzenleme değil) — çağrı yerinde otomatik seçim için (sürücü picker'ı tc/phone snapshot'lar). */
  onCreated?: (company: { id: string; code: string; name: string; tc?: string | null; phone?: string | null }) => void;
  /** DÜZENLEME sonrası — çağrı yeri seçili sürücü-cariyi düzenlediyse snapshot'ı (ad/tc/phone) tazelemek için. */
  onSaved?: (company: { id: string; name: string; tc?: string | null; phone?: string | null }) => void;
}

interface FormState {
  name: string;
  taxNumber: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  isSupplier: boolean;
  isCustomer: boolean;
  isCarrier: boolean;
  isDriver: boolean;
  tc: string;
  licenseNo: string;
  notes: string;
}

const EMPTY: FormState = {
  name: "", taxNumber: "", email: "", phone: "", address: "", city: "", country: "TR",
  isSupplier: false, isCustomer: false, isCarrier: false, isDriver: false, tc: "", licenseNo: "", notes: "",
};

interface PendingDoc { filename: string; dataBase64: string }

export function CompanyFormModal({ open, companyId, defaultRole, onClose, onCreated, onSaved }: CompanyFormModalProps) {
  const isEdit = !!companyId;
  const [state, setState] = useState<FormState>(EMPTY);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const tp = useTesisPicker(!isEdit);
  const [tesisPick, setTesisPick] = useState("");

  // Sürücü evrakları: düzenlemede sunucudaki (docs), yeni kayıtta oluşturma sonrası yüklenecekler (pending).
  const [docs, setDocs] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingDoc[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null); // create başarılı ama evrak yüklemesi patlarsa cari YENİDEN oluşmasın
  const fileRef = useRef<HTMLInputElement>(null);

  const detailQ = trpc.company.get.useQuery(
    { id: companyId ?? "" },
    { enabled: open && isEdit, staleTime: 0 },
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const d = detailQ.data;
      if (d) {
        setState({
          name: d.name,
          taxNumber: d.taxNumber ?? "",
          email: d.email ?? "",
          phone: d.phone ?? "",
          address: d.address ?? "",
          city: d.city ?? "",
          country: d.country ?? "TR",
          isSupplier: d.isSupplier,
          isCustomer: d.isCustomer,
          isCarrier: d.isCarrier,
          isDriver: (d as { isDriver?: boolean }).isDriver ?? false,
          tc: (d as { tc?: string | null }).tc ?? "",
          licenseNo: (d as { licenseNo?: string | null }).licenseNo ?? "",
          notes: (d as { notes?: string | null }).notes ?? "",
        });
        setDocs((d as { documents?: string[] }).documents ?? []);
        setSubmitErr(null);
      }
    } else {
      setState({ ...EMPTY, isSupplier: defaultRole === "supplier", isCustomer: defaultRole === "customer", isCarrier: defaultRole === "carrier", isDriver: defaultRole === "driver" });
      setDocs([]); setPending([]); setCreatedId(null);
      setSubmitErr(null);
      setTesisPick("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, detailQ.data, defaultRole]);

  // Yeni kayıtta canlı kod önizlemesi (CAR-{ad}-{sıra}); düzenlemede mevcut kod sabit.
  const [debouncedName, setDebouncedName] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(state.name.trim()), 300);
    return () => clearTimeout(t);
  }, [state.name]);
  const codePreviewQ = trpc.company.codePreview.useQuery(
    { name: debouncedName },
    { enabled: open && !isEdit && debouncedName.length > 0, staleTime: 3_000 },
  );
  const previewCode = isEdit ? (detailQ.data?.code ?? "") : (codePreviewQ.data?.code ?? "");

  const upsert = trpc.company.upsert.useMutation();
  const uploadMut = trpc.company.uploadDocument.useMutation();
  const removeDocMut = trpc.company.removeDocument.useMutation();

  const errorMessage = useMemo(() => {
    if (submitErr) return submitErr;
    const e = upsert.error ?? uploadMut.error;
    if (!e) return null;
    const zod = (e.data as { zodError?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] } } | undefined)?.zodError;
    if (zod) {
      const msgs = [...Object.values(zod.fieldErrors ?? {}).flat(), ...(zod.formErrors ?? [])].filter(Boolean) as string[];
      if (msgs.length) return msgs.join(" · ");
    }
    return e.message ?? "Bilinmeyen hata";
  }, [submitErr, upsert.error, uploadMut.error]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  function onPickDoc(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setSubmitErr(`"${file.name}" 8 MB'tan büyük — yüklenemez.`); return; }
    setSubmitErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      const dataBase64 = String(reader.result);
      const targetId = companyId ?? createdId;
      if (targetId) {
        // Düzenleme (veya create sonrası): doğrudan yükle.
        uploadMut.mutate({ id: targetId, filename: file.name, dataBase64 }, {
          onSuccess: (r) => setDocs((prev) => [...prev, r.url]),
          onError: (er) => setSubmitErr(er.message),
        });
      } else {
        // Yeni kayıt henüz oluşmadı → kuyruğa al, "Oluştur"da yüklenir.
        setPending((p) => [...p, { filename: file.name, dataBase64 }]);
      }
    };
    reader.readAsDataURL(file);
  }
  function removeDoc(url: string) {
    const targetId = companyId ?? createdId;
    if (!targetId) return;
    removeDocMut.mutate({ id: targetId, url }, { onSuccess: () => setDocs((prev) => prev.filter((u) => u !== url)) });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitErr(null);
    if (!state.name.trim()) return setSubmitErr("Firma adı zorunlu.");
    if (!state.isSupplier && !state.isCustomer && !state.isCarrier && !state.isDriver) return setSubmitErr("En az bir rol seçin (Tedarikçi, Müşteri, Nakliyeci veya Sürücü).");
    if (tp.show && !tesisPick) return setSubmitErr("Tesis seçin (Global dahil).");

    try {
      const payload = {
        name: state.name.trim(),
        taxNumber: state.taxNumber || undefined,
        email: state.email || undefined,
        phone: state.phone || undefined,
        address: state.address || undefined,
        city: state.city || undefined,
        country: state.country || "TR",
        isActive: true,
        isSupplier: state.isSupplier,
        isCustomer: state.isCustomer,
        isCarrier: state.isCarrier,
        isDriver: state.isDriver,
        tc: state.isDriver ? (state.tc || undefined) : undefined,
        licenseNo: state.isDriver ? (state.licenseNo || undefined) : undefined,
        notes: state.notes || undefined,
      };

      // Create başarılı ama evrak yüklemesi patlamışsa → cari YENİDEN oluşmasın (createdId).
      const targetId = companyId ?? createdId;
      const res = targetId
        ? await upsert.mutateAsync({ id: targetId, tesisId: resolveTesisId(tp.show, tesisPick), ...payload })
        : await upsert.mutateAsync({ tesisId: resolveTesisId(tp.show, tesisPick), ...payload });
      if (!targetId) setCreatedId(res.id);

      // Yeni kayıtta kuyruğa alınan sürücü evraklarını oluşan cariye yükle.
      // Kısmi hata: başarılıyı HEMEN kuyruktan çıkar → resubmit yalnız yüklenemeyenleri dener (mükerrer kopya olmaz).
      if (!companyId && pending.length > 0) {
        let uploaded = 0;
        try {
          for (const p of pending) {
            await uploadMut.mutateAsync({ id: res.id, filename: p.filename, dataBase64: p.dataBase64 });
            uploaded++;
          }
          setPending([]);
        } catch (err) {
          setPending(pending.slice(uploaded)); // yalnız yüklenemeyenler kalsın → resubmit mükerrer yüklemez
          throw err;
        }
      }

      await qc.invalidateQueries();
      const tcSnap = state.isDriver ? (state.tc || null) : null;
      if (!isEdit && res) onCreated?.({ id: res.id, code: res.code, name: res.name, tc: tcSnap, phone: state.phone || null });
      else if (res) onSaved?.({ id: res.id, name: res.name, tc: tcSnap, phone: state.phone || null }); // düzenleme → çağrı yeri seçili sürücüyse snapshot tazeler
      onClose();
    } catch {
      /* errorMessage gösterilir */
    }
  }

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      widthPx={780} /* md (520) + %50 — çok rollü form + sürücü evrak kartları için geniş */
      title={isEdit ? "Cari Düzenle" : "Yeni Cari"}
      subtitle={isEdit ? `Kod: ${previewCode || "—"}` : "Firma/kişi kimliği + rol (Tedarikçi / Müşteri / Nakliyeci / Sürücü)"}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={upsert.isPending}>İptal</button>
          <button type="submit" form="company-form" className="btn-md primary" disabled={upsert.isPending}>
            {upsert.isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
          </button>
        </>
      }
    >
      <form id="company-form" onSubmit={handleSubmit} noValidate>
        {errorMessage ? <div className="form-banner-err">{errorMessage}</div> : null}
        <TesisPickerField show={tp.show} facilities={tp.facilities} value={tesisPick} onChange={setTesisPick} />

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cf-code">
              Cari Kodu {!isEdit
                ? <span style={{ fontSize: 10.5, color: "var(--neutral-400)", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(otomatik)</span>
                : null}
            </label>
            <input
              id="cf-code"
              className="form-input"
              type="text"
              value={previewCode}
              placeholder={!isEdit ? "Ad girince üretilir…" : ""}
              readOnly
              disabled={isEdit}
              aria-readonly="true"
              style={{ background: "var(--neutral-50)", color: "var(--neutral-800)", fontFamily: "var(--font-mono)", fontWeight: 600, cursor: "default" }}
            />
            <span className="help">{isEdit ? "Cari kodu değiştirilemez." : "Adın sessiz harfleri + sıra no ile otomatik üretilir."}</span>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cf-name">{state.isDriver && !state.isSupplier && !state.isCustomer && !state.isCarrier ? "Ad Soyad" : "Firma Adı"} <span className="req">*</span></label>
            <input
              id="cf-name"
              className="form-input"
              type="text"
              value={state.name}
              onChange={(e) => setField("name", e.target.value)}
              maxLength={200}
              required
              autoComplete="off"
            />
          </div>
        </div>

        {/* Roller */}
        <div className="form-field">
          <label className="lbl">Roller <span className="req">*</span></label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <RoleToggle active={state.isSupplier} icon="truck" label="Tedarikçi" onToggle={() => setField("isSupplier", !state.isSupplier)} />
            <RoleToggle active={state.isCustomer} icon="users" label="Müşteri" onToggle={() => setField("isCustomer", !state.isCustomer)} />
            <RoleToggle active={state.isCarrier} icon="truck" label="Nakliyeci" onToggle={() => setField("isCarrier", !state.isCarrier)} />
            <RoleToggle active={state.isDriver} icon="user" label="Sürücü" onToggle={() => setField("isDriver", !state.isDriver)} />
          </div>
          <span className="help">
            Bir kayıt birden çok rol taşıyabilir. Tedarikçi/Müşteri için ayrı kod üretilir (SUP- / CUS-); Nakliyeci sevkiyatta taşıyıcı, Sürücü sevkiyatta sürücü olarak listelenir.
            {isEdit ? " Bağımlı kaydı olan bir rol kaldırılamaz." : ""}
          </span>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cf-tax">Vergi No</label>
            <input id="cf-tax" className="form-input" type="text" value={state.taxNumber} onChange={(e) => setField("taxNumber", e.target.value)} maxLength={50} inputMode="numeric" />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cf-email">E-posta</label>
            <input id="cf-email" className="form-input" type="email" value={state.email} onChange={(e) => setField("email", e.target.value)} autoComplete="email" inputMode="email" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cf-phone">Telefon</label>
            <input id="cf-phone" className="form-input" type="tel" value={state.phone} onChange={(e) => setField("phone", e.target.value)} autoComplete="tel" inputMode="tel" />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cf-city">Şehir</label>
            <input id="cf-city" className="form-input" type="text" value={state.city} onChange={(e) => setField("city", e.target.value)} autoComplete="address-level2" />
          </div>
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="cf-address">Adres</label>
          <textarea id="cf-address" className="form-textarea" value={state.address} onChange={(e) => setField("address", e.target.value)} maxLength={500} rows={2} />
        </div>

        <div className="form-field" style={{ maxWidth: 160 }}>
          <label className="lbl" htmlFor="cf-country">Ülke</label>
          <input id="cf-country" className="form-input" type="text" value={state.country} onChange={(e) => setField("country", e.target.value)} maxLength={100} />
        </div>

        {/* Sürücü rolü alanları — yalnız Sürücü işaretliyken */}
        {state.isDriver ? (
          <div style={{ marginTop: 6, paddingTop: 14, borderTop: "1px solid var(--neutral-200)" }}>
            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="cf-tc">TC No</label>
                <input id="cf-tc" className="form-input" type="text" value={state.tc} onChange={(e) => setField("tc", e.target.value)} maxLength={20} inputMode="numeric" placeholder="11 haneli" style={{ fontFamily: "var(--font-mono)" }} />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="cf-license">Ehliyet No</label>
                <input id="cf-license" className="form-input" type="text" value={state.licenseNo} onChange={(e) => setField("licenseNo", e.target.value)} maxLength={50} style={{ fontFamily: "var(--font-mono)" }} />
              </div>
            </div>

            <div className="form-field" style={{ marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <span className="lbl" style={{ display: "block", margin: 0 }}>Sürücü Evrakları</span>
                  <span className="help" style={{ display: "block", fontWeight: 400 }}>Foto / PDF · ≤8 MB — sevkiyatta seçilince otomatik eklenir</span>
                </div>
                <span style={{ flex: 1 }} />
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" hidden onChange={onPickDoc} disabled={uploadMut.isPending} />
                <button type="button" className="btn-md secondary" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending} style={{ flexShrink: 0 }}>
                  <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : "Evrak Ekle"}
                </button>
              </div>

              {docs.length === 0 && pending.length === 0 ? (
                <div style={{ padding: "18px 14px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12.5, color: "var(--neutral-500)", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <Icon name="fileText" size={20} />
                  Henüz evrak yok — fotoğraf veya PDF ekleyebilirsiniz.
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {docs.map((url) => (
                    <DocChip key={url} url={url} onOpen={setPreviewUrl} onRemove={() => removeDoc(url)} busy={removeDocMut.isPending} />
                  ))}
                  {pending.map((p, i) => (
                    <div key={`${p.filename}-${i}`} style={{ display: "flex", flexDirection: "column", gap: 3, width: 116 }}>
                      <DocChip
                        url={p.dataBase64}
                        isPdf={p.filename.toLowerCase().endsWith(".pdf")}
                        label={p.filename}
                        onOpen={setPreviewUrl}
                        onRemove={() => setPending((prev) => prev.filter((_, j) => j !== i))}
                      />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--warning)", display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <span style={{ width: 5, height: 5, borderRadius: 999, background: "var(--warning)", display: "inline-block" }} />Yüklenecek
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {!isEdit && pending.length > 0 ? <span className="help" style={{ display: "block", marginTop: 8 }}>Evraklar "Oluştur" sonrası cari kartına yüklenecek.</span> : null}
            </div>

            <div className="form-field">
              <label className="lbl" htmlFor="cf-notes">Not</label>
              <input id="cf-notes" className="form-input" type="text" value={state.notes} onChange={(e) => setField("notes", e.target.value)} maxLength={1000} />
            </div>
          </div>
        ) : null}
      </form>
    </Drawer>
    <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </>
  );
}

function RoleToggle({ active, icon, label, onToggle }: { active: boolean; icon: string; label: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={active}
      onClick={onToggle}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px",
        borderRadius: "var(--radius-md, 8px)", cursor: "pointer", fontSize: 13, fontWeight: 600,
        border: `1.5px solid ${active ? "var(--primary)" : "var(--neutral-300)"}`,
        background: active ? "var(--primary-subtle, #EAEEF5)" : "var(--surface, #fff)",
        color: active ? "var(--primary)" : "var(--neutral-600)",
        transition: "all 150ms cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 16, height: 16, borderRadius: 4,
        border: `1.5px solid ${active ? "var(--primary)" : "var(--neutral-400)"}`,
        background: active ? "var(--primary)" : "transparent",
        color: "#fff",
      }}>
        {active ? <Icon name="check" size={12} /> : null}
      </span>
      <Icon name={icon} size={15} />
      {label}
    </button>
  );
}
